import { useHostPageIds } from "@/features/standalone-versions";
import Popover from "@components/ui/popover";
import { ScrollArea } from "@components/ui/scroll-area";
import { cn } from "@lib/cn";
import { useAnalyticsQuery } from "@pages/analytics/hooks";
import { useAnalyticsStoreShallow } from "@pages/analytics/store/analytics.store";
import { ANALYTICS_QUERY, type DataByDate } from "@pages/analytics/types";
import {
  IconCheck,
  IconSearch,
  IconSelector,
  IconX,
} from "@tabler/icons-react";
import { memo, useCallback, useEffect, useMemo, useState } from "react";

export const StoriesFilter = memo(() => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const { story, setStory } = useAnalyticsStoreShallow((s) => ({
    story: s.story,
    setStory: s.setStory,
  }));

  const { data: response, isLoading } = useAnalyticsQuery({
    query: [
      ANALYTICS_QUERY.READERS_BY_DATE,
      ANALYTICS_QUERY.READERS_BY_LOCATION,
    ],
    enabled: isOpen,
  });

  const hostPageIds = useHostPageIds();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 250);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  const handleStoryChange = useCallback(
    (s: DataByDate) => {
      setStory({
        id: s.story_id,
        title: s.story_title,
      });
      setIsOpen(false);
    },
    [setStory],
  );

  const handleClear = useCallback(() => {
    setStory({ id: "", title: "" });
    setIsOpen(false);
  }, [setStory]);

  const readersByDate = response?.[ANALYTICS_QUERY.READERS_BY_DATE] as
    | DataByDate[]
    | undefined;

  const stories = useMemo(() => {
    if (!readersByDate) return [];

    // Get unique stories by story_id
    const uniqueStories = Array.from(
      new Map(readersByDate.map((s: DataByDate) => [s.story_id, s])).values(),
    ) as DataByDate[];

    // A standalone version's sessions are filed under its host page, which is
    // not a story and does not belong in a list of them (see
    // features/standalone-versions). The versions keep their own row in the
    // version filter, their sessions and every total.
    return uniqueStories.filter((s) => !hostPageIds.has(s.story_id));
  }, [readersByDate, hostPageIds]);

  // Filter cached stories by search term
  const filteredStories = useMemo(() => {
    if (!debouncedSearchTerm.trim()) return stories;
    const search = debouncedSearchTerm.toLowerCase();
    return stories.filter((s: DataByDate) =>
      s.story_title.toLowerCase().includes(search),
    );
  }, [stories, debouncedSearchTerm]);

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setTimeout(() => {
        setSearchTerm("");
        setDebouncedSearchTerm("");
      }, 250);
    }
  }, []);

  return (
    <Popover
      open={isOpen}
      onOpenChange={handleOpenChange}
      placement="bottom-start"
      triggerWidth
    >
      <Popover.Trigger
        className={cn(
          "tw-flex tw-h-9 tw-cursor-pointer active:!tw-outline-offset-0 active:!tw-outline-1 active:!tw-outline-primary tw-items-center tw-shadow-2xs tw-gap-1 tw-rounded-lg tw-outline !tw-outline-1 !tw-outline-blue/5 tw-bg-white tw-px-2 tw-transition-colors !tw-outline-offset-0 tw-duration-100 hover:!tw-outline-primary tw-max-w-40 -xl:tw-w-full -xl:tw-max-w-[700px]",
          {
            "!tw-outline-primary": isOpen || !!story.id,
          },
        )}
      >
        <span className="tw-block tw-px-1 tw-font-sans tw-text-sm tw-font-medium tw-text-blue tw-truncate">
          {story.title || "All stories"}
        </span>
        <IconSelector className="tw-size-4 tw-text-blue-500 tw-ml-auto" />
        {story.id && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                handleClear();
              }
            }}
            role="button"
            tabIndex={0}
            className="tw-ml-1 tw-flex tw-h-full tw-items-center tw-justify-center tw-border-l tw-border-solid tw-border-primary tw-pl-2"
          >
            <IconX className="tw-size-4 tw-text-primary" />
          </div>
        )}
      </Popover.Trigger>
      <Popover.Content className="tw-rounded-lg !tw-w-auto tw-outline !tw-outline-1 !tw-outline-blue/5 tw-bg-white tw-shadow-md">
        <div className="tw-relative tw-h-9 tw-px-3 tw-border-b tw-border-b-solid tw-border-b-blue-100">
          <IconSearch className="tw-size-4 tw-text-blue-500 tw-absolute tw-top-1/2 -tw-translate-y-1/2 tw-left-3" />
          <input
            type="text"
            placeholder="Search story"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="tw-pl-6 tw-outline-none tw-w-full tw-h-full tw-rounded-t-lg tw-text-sm tw-font-normal tw-text-blue placeholder:tw-text-blue-500"
          />
        </div>
        <ScrollArea maxHeight={250}>
          <div className="tw-p-1 tw-space-y-0.5 tw-max-w-full tw-min-w-0">
            {isLoading ? (
              <StoriesSkeleton />
            ) : filteredStories.length === 0 ? (
              <div className="tw-py-3 tw-text-center tw-text-sm tw-text-blue-500">
                {debouncedSearchTerm
                  ? "No stories found"
                  : "No stories available"}
              </div>
            ) : (
              filteredStories.map((s: DataByDate) => (
                <button
                  type="button"
                  key={s.story_id}
                  onClick={() => handleStoryChange(s)}
                  className={cn(
                    "tw-flex tw-h-9 tw-w-52 tw-cursor-pointer tw-items-center tw-justify-between tw-rounded tw-px-2 tw-font-sans tw-text-sm tw-text-blue tw-outline-none tw-transition-colors hover:tw-bg-blue-50",
                    {
                      "tw-bg-blue-100 hover:tw-bg-blue-100":
                        story.id === s.story_id,
                    },
                  )}
                >
                  <span className="tw-flex-1 tw-min-w-0 tw-text-left tw-truncate tw-whitespace-nowrap">
                    {s.story_title}
                  </span>
                  {story.id === s.story_id && (
                    <IconCheck className="tw-size-4 tw-text-blue tw-ml-3 tw-flex-shrink-0" />
                  )}
                </button>
              ))
            )}
          </div>
        </ScrollArea>
      </Popover.Content>
    </Popover>
  );
});

const STORIES_SKELETON_WIDTHS = [72, 95, 38, 110, 56];

const StoriesSkeleton = () => {
  return (
    <>
      {STORIES_SKELETON_WIDTHS.map((width) => (
        <div
          key={width}
          className="tw-flex tw-h-9 tw-w-full tw-items-center tw-justify-between tw-rounded tw-px-2 tw-animate-skeleton-loading"
        >
          <span
            className="tw-block tw-h-2 tw-w-24 tw-rounded-lg tw-bg-blue-100"
            style={{ width: `${width}%` }}
          />
        </div>
      ))}
    </>
  );
};
